<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-green-500 to-teal-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Tipos de Animales</h1>
          <p class="text-green-100 mt-1">Gestiona los tipos de animales disponibles en el sistema</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Tipos</p>
            <p class="text-2xl font-semibold text-gray-900">{{ animalTypes?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Razas</p>
            <p class="text-2xl font-semibold text-gray-900">{{ totalBreeds }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions Bar -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div class="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <!-- Search Section -->
        <div class="flex-1 max-w-md">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar tipos de animales</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              v-model="globalFilter"
              type="text"
              class="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Escribe para buscar..."
            />
          </div>
        </div>
        
        <!-- Add Button -->
        <div class="flex-shrink-0">
          <button
            @click="router.push('/animal-types/create')"
            class="inline-flex items-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Agregar Tipo
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        v-model:filters="filters"
        :value="animalTypes"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        tableStyle="min-width: 50rem"
        filterDisplay="menu"
        :globalFilterFields="['name', 'description']"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Tipos de Animales</h2>
            <IconField iconPosition="left">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="filters['global'].value" placeholder="Buscar tipos..." />
            </IconField>
          </div>
        </template>
        
        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay tipos de animales</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza agregando un nuevo tipo de animal.</p>
          </div>
        </template>
        
        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p class="mt-2 text-gray-600">Cargando tipos de animales...</p>
          </div>
        </template>

        <Column field="name" header="Nombre" sortable>
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-10 w-10 rounded-full overflow-hidden mr-3 cursor-pointer" @click="openImageModal(data.image || '/images/empty_image.png', data.name)">
                <img 
                  :src="data.image || '/images/empty_image.png'" 
                  :alt="data.name"
                  class="h-full w-full object-cover"
                  @error="$event.target.src = '/images/empty_image.png'"
                />
              </div>
              <div>
                <div class="text-sm font-medium text-gray-900">{{ data.name }}</div>
                <div class="text-sm text-gray-500">{{ data.breeds?.length || 0 }} razas</div>
              </div>
            </div>
          </template>
        </Column>

        <Column field="breeds" header="Razas">
          <template #body="{ data }">
            <div class="flex flex-wrap gap-1 max-w-xs">
              <template v-if="data.breeds && data.breeds.length > 0">
                <template v-for="(breed, index) in data.breeds.slice(0, 3)" :key="breed.id">
                  <span 
                    @click="showAllBreeds(data)"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors duration-200"
                  >
                    {{ breed.name }}
                  </span>
                </template>
                <button 
                  v-if="data.breeds.length > 3"
                  @click="showAllBreeds(data)"
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors duration-200"
                >
                  +{{ data.breeds.length - 3 }} más
                </button>
              </template>
              <span v-else class="text-xs text-gray-500 italic">Sin razas</span>
            </div>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha de Creación" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-500">
              {{ new Date(data.createdAt).toLocaleDateString('es-ES') }}
            </span>
          </template>
        </Column>

        <Column header="Acciones" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="editAnimalType(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style="background-color: #1d4ed8; color: #fff"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                @click="deleteAnimalType(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style="background-color: #be123c; color: #fff"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Confirm Delete Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="'Eliminar Tipo de Animal'"
      :message="`¿Estás seguro de que quieres eliminar el tipo '${animalTypeToDelete?.name}'? Esta acción eliminará también todas las razas asociadas y no se puede deshacer.`"
      :confirm-text="'Eliminar'"
      :cancel-text="'Cancelar'"
      @confirm="confirmDelete"
      @close="closeConfirmModal"
    />

    <!-- Image Modal -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImage"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />

    <!-- Modal para mostrar todas las razas -->
    <div v-if="showBreedsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <!-- Header del modal -->
        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold text-white flex items-center">
              <i class="pi pi-list mr-2"></i>
              Razas de {{ selectedAnimalTypeForBreeds?.name }}
            </h3>
            <button 
              @click="closeBreedsModal" 
              class="text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            >
              <i class="pi pi-times text-lg"></i>
            </button>
          </div>
        </div>
        
        <!-- Contenido del modal -->
        <div class="p-6 overflow-y-auto max-h-[60vh]">
          <template v-if="selectedAnimalTypeForBreeds?.breeds && selectedAnimalTypeForBreeds.breeds.length > 0">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div 
                v-for="breed in selectedAnimalTypeForBreeds.breeds" 
                :key="breed.id"
                class="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3 hover:shadow-md transition-all duration-200 hover:scale-105"
              >
                <div class="flex items-center">
                  <i class="pi pi-tag text-blue-600 mr-2"></i>
                  <span class="font-medium text-blue-800">{{ breed.name }}</span>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="text-center py-8">
            <i class="pi pi-info-circle text-4xl text-gray-400 mb-3"></i>
            <p class="text-gray-500 text-lg">No hay razas registradas para este tipo de animal.</p>
          </div>
        </div>
        
        <!-- Footer del modal -->
        <div class="bg-gray-50 px-6 py-4 border-t">
          <div class="flex justify-end">
            <button 
              @click="closeBreedsModal"
              class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center"
            >
              <i class="pi pi-times mr-2"></i>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotifications } from '../composables/useNotifications'
import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import ConfirmModal from '../components/ConfirmModal.vue'
import ImageModal from '../components/ImageModal.vue'
import animalTypeService from '../services/animalTypeService'

const router = useRouter()
const authStore = useAuthStore()
const { showSuccess, showError } = useNotifications()

// Reactive data
const animalTypes = ref([])
const loading = ref(false)

// Filters
const globalFilter = ref('')

// PrimeVue filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS }
})

const showConfirmModal = ref(false)
const showImageModal = ref(false)
const showBreedsModal = ref(false)
const animalTypeToDelete = ref(null)
const selectedImage = ref('')
const selectedImageAlt = ref('')
const selectedAnimalTypeForBreeds = ref(null)

// Computed properties
const totalBreeds = computed(() => {
  return animalTypes.value.reduce((total, type) => total + (type.breeds?.length || 0), 0)
})

// Watch for filter changes
watch(globalFilter, (newValue) => {
  filters.value.global.value = newValue
})

// Methods
const loadAnimalTypes = async () => {
  try {
    loading.value = true
    const response = await animalTypeService.getAllAnimalTypes()
    animalTypes.value = response.data
  } catch (error) {
    console.error('Error loading animal types:', error)
    showError('Error al cargar los tipos de animales')
  } finally {
    loading.value = false
  }
}

const editAnimalType = (animalType) => {
  router.push(`/animal-types/${animalType.id}/edit`)
}

const deleteAnimalType = (animalType) => {
  animalTypeToDelete.value = animalType
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
  animalTypeToDelete.value = null
}

const openImageModal = (imageUrl, imageAlt) => {
  selectedImage.value = imageUrl
  selectedImageAlt.value = imageAlt
  showImageModal.value = true
}

const closeImageModal = () => {
  showImageModal.value = false
  selectedImage.value = ''
  selectedImageAlt.value = ''
}

const showAllBreeds = (animalType) => {
  selectedAnimalTypeForBreeds.value = animalType
  showBreedsModal.value = true
}

const closeBreedsModal = () => {
  showBreedsModal.value = false
  selectedAnimalTypeForBreeds.value = null
}

const confirmDelete = async () => {
  try {
    await animalTypeService.deleteAnimalType(animalTypeToDelete.value.id)
    showSuccess('Tipo de animal eliminado exitosamente')
    closeConfirmModal()
    await loadAnimalTypes()
  } catch (error) {
    console.error('Error deleting animal type:', error)
    showError('Error al eliminar el tipo de animal')
  }
}

// Initialize data
onMounted(async () => {
  await loadAnimalTypes()
})
</script>

<style scoped>
.card {
  @apply bg-white rounded-lg shadow-sm border border-gray-200;
}

:deep(.p-datatable) {
  border-radius: 0.5rem;
}

:deep(.p-datatable .p-datatable-header) {
  background: #f9fafb;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  padding: 1rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #f9fafb;
}

:deep(.p-paginator) {
  background: #f9fafb;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 0.75rem 1rem;
}
</style>